import * as React from 'react';
import { ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { CheckSolidIcon } from '../icons/Check';
import { SelectContext } from './Select';
import {
  selectOptionWrapperVariants,
  selectOptionContentVariants,
  selectSectionTitleVariants,
  selectDividerVariants,
  selectSupportingTextVariants,
  selectOptionIconVariants,
  selectVerticalContentVariants,
  selectCheckIconVariants,
  selectOptionButtonVariants,
  selectOptionButtonIconVariants,
  SELECT_OPTION_ARRANGEMENT,
  SELECT_OPTION_TYPE,
  type SelectOptionArrangement,
  type SelectOptionType,
  type SelectOptionState
} from './styles';

export interface SelectOptionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  /**
   * Option type
   */
  type?: SelectOptionType;
  /**
   * Layout arrangement
   */
  arrangement?: SelectOptionArrangement;
  /**
   * Visual state
   */
  state?: SelectOptionState;
  /**
   * Main text content
   */
  children?: ReactNode;
  /**
   * Supporting text (for vertical arrangement)
   */
  supportingText?: string;
  /**
   * Leading icon
   */
  icon?: ReactNode;
  /**
   * Whether to show supporting text
   */
  showSupportingText?: boolean;
  /**
   * Whether the option is selected.
   * When the parent Select has `onChange`, this is auto-detected from `value` match.
   */
  selected?: boolean;
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
  /**
   * Option identifier value.
   * When provided with a parent Select that has `onChange`,
   * clicking this option calls `onChange(value)` and the display text
   * is auto-resolved from children.
   */
  value?: string;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Button label (for button type)
   */
  buttonLabel?: string;
  /**
   * Button icon (for button type)
   */
  buttonIcon?: ReactNode;
  /**
   * Button click handler (for button type)
   */
  onButtonClick?: () => void;
  /**
   * ID for testing
   */
  dataTestId?: string;
}

export const SelectOption = React.forwardRef<HTMLDivElement, SelectOptionProps>(
  (
    {
      type = SELECT_OPTION_TYPE.DEFAULT,
      arrangement = SELECT_OPTION_ARRANGEMENT.HORIZONTAL,
      state = 'default',
      children,
      supportingText,
      icon,
      showSupportingText = false,
      selected = false,
      disabled = false,
      value: optionValue,
      onClick,
      buttonLabel,
      buttonIcon,
      onButtonClick,
      className,
      dataTestId = 'select-option',
      ...props
    },
    ref
  ) => {
    const context = React.useContext(SelectContext);

    // Register label for display text resolution
    React.useEffect(() => {
      if (context?.hasOnChange && optionValue !== undefined) {
        context.registerLabel(optionValue, children);
      }
    }, [context, optionValue, children]);

    // Auto-detect selected state from context
    const isSelected = (context?.hasOnChange && optionValue !== undefined)
      ? context.selectedValue === optionValue
      : selected;

    // Divider type
    if (type === SELECT_OPTION_TYPE.DIVIDER) {
      return (
        <div
          ref={ref}
          role="separator"
          className={cn(selectOptionWrapperVariants({ type }), className)}
          data-testid={`${dataTestId}-divider`}
          {...props}>
          <div className={cn(selectDividerVariants())} />
        </div>
      );
    }

    // Section title type
    if (type === SELECT_OPTION_TYPE.SECTION_TITLE) {
      return (
        <div
          ref={ref}
          className={cn(selectOptionWrapperVariants({ type }), className)}
          data-testid={`${dataTestId}-section-title`}
          {...props}>
          <div className={cn(selectSectionTitleVariants())}>{children}</div>
        </div>
      );
    }

    // Button type
    if (type === SELECT_OPTION_TYPE.BUTTON) {
      const handleButtonClick = () => {
        if (disabled) return;
        onButtonClick?.();
      };

      const buttonState = state === 'hover' ? 'hover' : 'default';

      return (
        <div
          ref={ref}
          className={cn(selectOptionWrapperVariants({ type }), className)}
          data-testid={`${dataTestId}-button`}
          {...props}>
          <div
            role="button"
            aria-disabled={disabled}
            className={cn(selectOptionButtonVariants({ state: buttonState }))}
            onClick={handleButtonClick}>
            {buttonIcon && (
              <span className={cn(selectOptionButtonIconVariants())}>{buttonIcon}</span>
            )}
            {buttonLabel && <span>{buttonLabel}</span>}
          </div>
        </div>
      );
    }

    const finalState = disabled ? 'disabled' : state;
    const hasIcon = type === SELECT_OPTION_TYPE.LEADING_ICON && icon;
    const isVertical = arrangement === SELECT_OPTION_ARRANGEMENT.VERTICAL;

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;

      // If context has onChange and this option has a value, delegate to context
      if (context?.hasOnChange && optionValue !== undefined) {
        context.onSelect(optionValue);
      }

      onClick?.(event);
    };

    return (
      <div
        ref={ref}
        className={cn(selectOptionWrapperVariants({ type }), className)}
        data-testid={dataTestId}
        {...props}>
        <div
          role="option"
          aria-selected={isSelected}
          aria-disabled={disabled}
          className={cn(selectOptionContentVariants({ arrangement, state: finalState }))}
          onClick={handleClick}>
          <div className={cn("flex gap-2xs flex-1 min-w-0", isVertical && showSupportingText ? "items-start" : "items-center")}>
            {hasIcon && <span className={cn(selectOptionIconVariants(), isVertical && showSupportingText && '!h-[theme(lineHeight.md)]')}>{icon}</span>}

            {isVertical && showSupportingText ? (
              <div className={cn(selectVerticalContentVariants())}>
                <div className="text-sm leading-md font-regular text-primary w-full">
                  {children}
                </div>
                {supportingText && (
                  <div className={cn(selectSupportingTextVariants())}>{supportingText}</div>
                )}
              </div>
            ) : (
              children
            )}
          </div>

          {isSelected && (
            <span className={cn(selectCheckIconVariants())} style={{ color: '#0596FF' }}>
              <CheckSolidIcon className="text-brand" />
            </span>
          )}
        </div>
      </div>
    );
  }
);

SelectOption.displayName = 'SelectOption';
